import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { Tag } from '../../../_service/data.service';
import { EXPORT_SORT_ORDER_OPTIONS, ExportSortOrder } from '../logic/bulk-actions.logic';

export type BulkActionType =
  | 'export'
  // Single-note DOCX export used by NoteViewBase's toolbar (unrelated to the
  // Home bulk "Export Notes..." action above) — kept separate/unchanged.
  | 'export-docx'
  | 'print'
  | 'set-status'
  | 'add-tags'
  | 'remove-tags'
  | 'move-to-space'
  | 'set-favorite'
  | 'delete';

export type ExportFormat = 'pdf' | 'docx' | 'json';

export type BulkActionParams = {
  exportFormat?: ExportFormat;
  sortOrder?: ExportSortOrder;
  fileName?: string;
  pageBreakBetweenNotes?: boolean;
  pageNumbers?: boolean;
  includeTableOfContents?: boolean;
  includeMetaData?: boolean;
  status?: string;
  tagIds?: string[];
  removeAllTags?: boolean;
  removeTagIds?: string[];
  updateTimestamp?: boolean;
  favorite?: boolean;
  /** tasks/space-move-client.md — the Space to move the selected notes to. */
  destinationSpaceId?: string;
};

/** One option in the grouped Add Tags picker (tasks/frontend-tagging.md): a Space or private tag. */
export interface BulkTagOption {
  _id: string;
  name: string;
  color?: string;
  /** "Private", or the Space the tag belongs to — keeps identically named tags distinguishable. */
  context: string;
  isPrivate: boolean;
}

export interface BulkTagOptionGroup {
  label: string;
  items: BulkTagOption[];
}

export interface BulkActionRequest {
  action: BulkActionType;
  params?: BulkActionParams;
}

export interface BulkActionConfig {
  id: BulkActionType;
  label: string;
  description?: string;
  requiresParams?: boolean;
}

@Component({
  selector: 'app-bulk-action-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, SelectModule, MultiSelectModule],
  template: `
    <p-dialog
      header="Bulk Action"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '28rem', maxWidth: '90vw' }"
      [draggable]="false"
      [resizable]="false"
      (onShow)="onDialogShow()"
      (onHide)="onCancel()"
      appendTo="body"
    >
      <ng-container *ngIf="action">
        <div class="space-y-3">
          <div class="text-sm text-surface-600">
            {{ action.description || 'Configure the action before applying it to the selected notes.' }}
          </div>
          <div class="text-xs text-surface-500">Selected notes: {{ selectedCount }}</div>

          <!-- Unified export params -->
          <div *ngIf="action.id === 'export'" class="space-y-3">
            <div>
              <label class="block text-sm font-medium mb-1">Format</label>
              <p-select
                [options]="exportFormats"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="params.exportFormat"
                appendTo="body"
                placeholder="Choose format"
              ></p-select>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">Sort order</label>
              <p-select
                [options]="sortOrderOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="params.sortOrder"
                appendTo="body"
                placeholder="Choose sort order"
              ></p-select>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">Filename</label>
              <input
                pInputText
                [(ngModel)]="params.fileName"
                [placeholder]="exportFilenamePlaceholder"
                class="w-full"
              />
              <div class="mt-1 text-xs text-surface-500">A timestamp is appended automatically.</div>
            </div>
            <div *ngIf="params.exportFormat !== 'json'" class="flex items-center gap-2">
              <input
                id="includeTableOfContents"
                type="checkbox"
                [(ngModel)]="params.includeTableOfContents"
                class="h-4 w-4"
              />
              <label for="includeTableOfContents" class="text-sm">Include table of contents</label>
            </div>
            <div *ngIf="params.exportFormat !== 'json' && showPageBreakBetweenNotesOption" class="flex items-center gap-2">
              <input
                id="pageBreakBetweenNotes"
                type="checkbox"
                [(ngModel)]="params.pageBreakBetweenNotes"
                class="h-4 w-4"
              />
              <label for="pageBreakBetweenNotes" class="text-sm">Start each record on a new page</label>
            </div>
            <div *ngIf="params.exportFormat !== 'json'" class="flex items-center gap-2">
              <input
                id="pageNumbers"
                type="checkbox"
                [(ngModel)]="params.pageNumbers"
                class="h-4 w-4"
              />
              <label for="pageNumbers" class="text-sm">Show page numbers</label>
            </div>
            <div *ngIf="params.exportFormat === 'docx'" class="flex items-center gap-2">
              <input
                id="includeMetaData"
                type="checkbox"
                [(ngModel)]="params.includeMetaData"
                class="h-4 w-4"
              />
              <label for="includeMetaData" class="text-sm">Include meta data</label>
            </div>
          </div>

          <!-- Single-note DOCX export params (NoteViewBase) -->
          <div *ngIf="action.id === 'export-docx'" class="space-y-3">
            <div>
              <label class="block text-sm font-medium mb-1">Filename</label>
              <input
                pInputText
                [(ngModel)]="params.fileName"
                placeholder="notes_export.docx"
                class="w-full"
              />
            </div>
            <div class="flex items-center gap-2">
              <input
                id="includeTableOfContentsDocx"
                type="checkbox"
                [(ngModel)]="params.includeTableOfContents"
                class="h-4 w-4"
              />
              <label for="includeTableOfContentsDocx" class="text-sm">Include table of contents</label>
            </div>
            <div class="flex items-center gap-2">
              <input
                id="pageNumbersDocx"
                type="checkbox"
                [(ngModel)]="params.pageNumbers"
                class="h-4 w-4"
              />
              <label for="pageNumbersDocx" class="text-sm">Show page numbers</label>
            </div>
            <div class="flex items-center gap-2">
              <input
                id="includeMetaDataDocx"
                type="checkbox"
                [(ngModel)]="params.includeMetaData"
                class="h-4 w-4"
              />
              <label for="includeMetaDataDocx" class="text-sm">Include meta data</label>
            </div>
          </div>

          <!-- Print params -->
          <div *ngIf="action.id === 'print'" class="space-y-3">
            <div class="flex items-center gap-2">
              <input
                id="includeTableOfContentsPrint"
                type="checkbox"
                [(ngModel)]="params.includeTableOfContents"
                class="h-4 w-4"
              />
              <label for="includeTableOfContentsPrint" class="text-sm">Include table of contents</label>
            </div>
            <div *ngIf="showPageBreakBetweenNotesOption" class="flex items-center gap-2">
              <input
                id="pageBreakBetweenNotesPrint"
                type="checkbox"
                [(ngModel)]="params.pageBreakBetweenNotes"
                class="h-4 w-4"
              />
              <label for="pageBreakBetweenNotesPrint" class="text-sm">Start each record on a new page</label>
            </div>
            <div class="flex items-center gap-2">
              <input
                id="pageNumbersPrint"
                type="checkbox"
                [(ngModel)]="params.pageNumbers"
                class="h-4 w-4"
              />
              <label for="pageNumbersPrint" class="text-sm">Show page numbers</label>
            </div>
          </div>

          <!-- Confirmation copy for destructive actions -->
          <div *ngIf="action.id === 'delete'" class="bg-surface-100 p-3 rounded">
            <div class="text-sm font-semibold text-red-600">Confirm move to trash</div>
            <div class="text-xs text-surface-600">
              {{ selectedCount }} note(s) will be moved to Trash. You can restore them from Trash for 30 days.
            </div>
          </div>

          <!-- Move to Space params (tasks/space-move-client.md) -->
          <div *ngIf="action.id === 'move-to-space'" class="space-y-2">
            <label for="bulkMoveDestination" class="block text-sm font-medium mb-1">Destination Space</label>
            <p-select
              inputId="bulkMoveDestination"
              [options]="moveDestinations"
              optionLabel="label"
              optionValue="id"
              [(ngModel)]="params.destinationSpaceId"
              appendTo="body"
              placeholder="Choose a Space"
              styleClass="w-full"
            ></p-select>
            <div *ngIf="!moveDestinations.length" class="text-xs text-surface-500">
              There is no Space you can move notes to.
            </div>
          </div>

          <!-- Set status params -->
          <div *ngIf="action.id === 'set-status'" class="space-y-3">
            <div>
              <label class="block text-sm font-medium mb-1">Target status</label>
              <p-select
                [options]="statusOptions"
                [filter]="true"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="params.status"
                appendTo="body"
                placeholder="Select status"
              ></p-select>
            </div>
            <div class="flex items-center gap-2">
              <input
                id="updateTimestampToggle"
                type="checkbox"
                [(ngModel)]="params.updateTimestamp"
                class="h-4 w-4"
              />
              <label for="updateTimestampToggle" class="text-sm">Update timestamp</label>
            </div>
          </div>

          <!-- Add tags params -->
          <div *ngIf="action.id === 'add-tags'" class="space-y-3">
            <div>
              <label class="block text-sm font-medium mb-1">Tags to add</label>
              <p-multiselect
                #addTagsMultiSelect
                [options]="addTagGroups.length ? addTagGroups : sortedTags"
                [group]="addTagGroups.length > 0"
                optionGroupLabel="label"
                optionGroupChildren="items"
                optionLabel="name"
                optionValue="_id"
                [(ngModel)]="params.tagIds"
                appendTo="body"
                panelStyleClass="bulk-add-tags-panel"
                placeholder="Select one or more tags"
                [filter]="true"
                [showClear]="true"
                [maxSelectedLabels]="3"
                [style]="{ width: '100%' }"
              >
                <ng-template let-tag pTemplate="item">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <span class="h-3 w-3 shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
                    <i *ngIf="tag.isPrivate" class="pi pi-lock !text-[0.625rem] opacity-70" aria-label="Private tag"></i>
                    <span class="truncate">{{ tag.name }}</span>
                    <span *ngIf="tag.context" class="ml-auto shrink-0 pl-2 text-xs text-surface-500">{{ tag.context }}</span>
                  </div>
                </ng-template>
              </p-multiselect>
            </div>
            <div *ngIf="addTagGroups.length" class="text-xs text-surface-500">
              Private tags are added for you only. A Space tag is added only to the selected notes in its Space.
            </div>
          </div>

          <!-- Remove tags params -->
          <div *ngIf="action.id === 'remove-tags'" class="space-y-3">
            <div class="rounded border border-surface-200 p-3 space-y-2">
              <div class="text-sm font-medium">Removal scope</div>
              <label class="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="removeTagScope"
                  [value]="true"
                  [(ngModel)]="params.removeAllTags"
                  class="mt-1"
                />
                <span>
                  <span class="block text-sm font-medium">Remove all tags</span>
                  <span class="block text-xs text-surface-500">Default. Clears all Space tags and all your private tags from the selected notes.</span>
                </span>
              </label>
              <label class="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="removeTagScope"
                  [value]="false"
                  [(ngModel)]="params.removeAllTags"
                  class="mt-1"
                />
                <span>
                  <span class="block text-sm font-medium">Remove selected tags only</span>
                  <span class="block text-xs text-surface-500">Only remove tags you choose below.</span>
                </span>
              </label>
            </div>

            <div *ngIf="params.removeAllTags === false">
              <label class="block text-sm font-medium mb-1">Tags to remove</label>
              <p-multiselect
                [options]="removeTagGroups.length ? removeTagGroups : sortedRemovableTags"
                [group]="removeTagGroups.length > 0"
                optionGroupLabel="label"
                optionGroupChildren="items"
                optionLabel="name"
                optionValue="_id"
                [(ngModel)]="params.removeTagIds"
                appendTo="body"
                placeholder="Select one or more tags"
                [filter]="true"
                [showClear]="true"
                [maxSelectedLabels]="3"
                [style]="{ width: '100%' }"
              >
                <ng-template let-tag pTemplate="item">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <span class="h-3 w-3 shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
                    <i *ngIf="tag.isPrivate" class="pi pi-lock !text-[0.625rem] opacity-70" aria-label="Private tag"></i>
                    <span class="truncate">{{ tag.name }}</span>
                    <span *ngIf="tag.context" class="ml-auto shrink-0 pl-2 text-xs text-surface-500">{{ tag.context }}</span>
                  </div>
                </ng-template>
              </p-multiselect>
              <div class="mt-2 text-xs text-surface-500" *ngIf="!removeTagGroups.length && sortedRemovableTags.length === 0">
                No removable tags were found in the current selection.
              </div>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2 w-full">
          <p-button label="Cancel" severity="secondary" (onClick)="onCancel()"></p-button>
          <p-button
            [label]="action?.id === 'add-tags' ? 'Add' : action?.id === 'remove-tags' ? 'Remove' : 'Apply'"
            [disabled]="!canSubmit()"
            (onClick)="onSubmit()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
})
export class BulkActionDialogComponent implements OnChanges {
  @ViewChild('addTagsMultiSelect') addTagsMultiSelect?: MultiSelect;
  @ViewChild('addTagsMultiSelect', { read: ElementRef }) addTagsMultiSelectEl?: ElementRef<HTMLElement>;

  @Input() visible = false;
  @Input() action: BulkActionConfig | null = null;
  @Input() statuses: string[] = [];
  @Input() tags: Tag[] = [];
  @Input() removableTags: Tag[] = [];
  /** Grouped Space/Private options for Remove Tags: the tags actually on the selected notes. */
  @Input() removeTagGroups: BulkTagOptionGroup[] = [];
  /** Grouped Space/Private options for Add Tags (falls back to `tags` when empty). */
  @Input() addTagGroups: BulkTagOptionGroup[] = [];
  /** Spaces the user may move notes into (write access). */
  @Input() moveDestinations: Array<{ id: string; label: string }> = [];
  @Input() selectedCount = 0;
  @Input() showPageBreakBetweenNotesOption = true;

  @Output() cancel = new EventEmitter<void>();
  @Output() submit = new EventEmitter<BulkActionRequest>();

  params: BulkActionParams = {};

  exportFormats: { label: string; value: ExportFormat }[] = [
    { label: 'PDF', value: 'pdf' },
    { label: 'DOCX', value: 'docx' },
    { label: 'JSON', value: 'json' },
  ];

  sortOrderOptions = EXPORT_SORT_ORDER_OPTIONS;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['action']?.currentValue) {
      this.resetParams(this.action?.id);
    }
  }

  private resetParams(actionId?: BulkActionType) {
    this.params = {
      exportFormat: 'pdf',
      sortOrder: 'default',
      fileName: actionId === 'export-docx' ? 'notes_export.docx' : '',
      includeTableOfContents: false,
      pageBreakBetweenNotes: true,
      pageNumbers: true,
      status: '',
      tagIds: [],
      removeAllTags: true,
      removeTagIds: [],
      updateTimestamp: true,
      destinationSpaceId: '',
    };
  }

  /** Base filename shown as a placeholder — the actual export always has a
   * `_YYYYMMDD-HHMMSS` timestamp appended by HomeComponent before download. */
  get exportFilenamePlaceholder(): string {
    return `notes_export.${this.params.exportFormat || 'pdf'}`;
  }

  get statusOptions() {
    return (this.statuses || []).map((s) => ({ label: s, value: s }));
  }

  get sortedTags(): Tag[] {
    return [...(this.tags || [])].sort((left, right) =>
      String(left?.name ?? '').localeCompare(String(right?.name ?? ''), undefined, { sensitivity: 'base' }),
    );
  }

  get sortedRemovableTags(): Tag[] {
    return [...(this.removableTags || [])].sort((left, right) =>
      String(left?.name ?? '').localeCompare(String(right?.name ?? ''), undefined, { sensitivity: 'base' }),
    );
  }

  canSubmit(): boolean {
    if (!this.action) {
      return false;
    }
    if (this.action.id === 'set-status') {
      return !!this.params.status;
    }
    if (this.action.id === 'move-to-space') {
      return !!this.params.destinationSpaceId;
    }
    if (this.action.id === 'add-tags') {
      return Array.isArray(this.params.tagIds) && this.params.tagIds.length > 0;
    }
    if (this.action.id === 'remove-tags') {
      if (this.params.removeAllTags !== false) {
        return true;
      }
      return Array.isArray(this.params.removeTagIds) && this.params.removeTagIds.length > 0;
    }
    return true;
  }

  onCancel() {
    this.cancel.emit();
  }

  onSubmit() {
    if (!this.action) {
      return;
    }
    this.submit.emit({
      action: this.action.id,
      params: { ...this.params },
    });
  }

  onDialogShow(): void {
    if (this.action?.id !== 'add-tags') {
      return;
    }

    // Open the tags dropdown and focus its built-in filter input.
    setTimeout(() => {
      (this.addTagsMultiSelect as any)?.show?.();

      // Fallback for PrimeNG variants where show() is not available.
      if (!(this.addTagsMultiSelect as any)?.overlayVisible) {
        this.addTagsMultiSelectEl?.nativeElement?.click();
      }

      setTimeout(() => {
        const filterInput = document.querySelector(
          '.bulk-add-tags-panel .p-multiselect-filter',
        ) as HTMLInputElement | null;

        if (filterInput) {
          filterInput.focus();
          filterInput.select();
        }
      }, 0);
    }, 0);
  }
}
